/** @typedef {{ schema: string, functionName: string, displayName?: string }} FunctionRef */

import { isChartLayoutItem } from './dashboardLayoutItems';
import { DEFAULT_DASHBOARD_SECTIONS } from './defaultDashboardConfig';

export const DASHBOARD_EXPORT_VERSION = 1;

/** Column keys hidden from chart table view and CSV export. */
export const HIDDEN_REPORT_COLUMNS = new Set([
    'organisationid',
    'organisation_id',
    'organizationid',
    'organization_id',
]);

export function isHiddenReportColumn(key) {
    if (!key) return false;
    return HIDDEN_REPORT_COLUMNS.has(String(key).toLowerCase());
}

export function filterVisibleColumns(columns = []) {
    return columns.filter(c => !isHiddenReportColumn(c.key));
}

function functionLookupKey(schema, functionName) {
    return `${schema ?? 'public'}.${functionName}`.toLowerCase();
}

function buildFunctionLookup(functions = []) {
    const byId = new Map();
    const byKey = new Map();
    for (const fn of functions) {
        const id = fn.id ?? fn.Id;
        if (id) byId.set(id, fn);
        const schema = fn.schema ?? fn.Schema ?? 'public';
        const name = fn.functionName ?? fn.FunctionName;
        if (name) byKey.set(functionLookupKey(schema, name), fn);
    }
    return { byId, byKey };
}

function lookupDefaultFunctionRef(dashboardName, chart) {
    const section = DEFAULT_DASHBOARD_SECTIONS.find(s => s.title === dashboardName);
    if (!section) return null;
    const match = section.charts.find(def =>
        def.title === chart.title
        && (def.dimensionFilter ?? null) === (chart.dimensionFilter ?? null));
    if (!match) return null;
    return { schema: 'public', functionName: match.functionName };
}

export function functionRefFromRegistered(fn) {
    if (!fn) return null;
    return {
        schema: fn.schema ?? fn.Schema ?? 'public',
        functionName: fn.functionName ?? fn.FunctionName,
        displayName: fn.displayName ?? fn.DisplayName,
    };
}

/**
 * Resolve stale chart functionIds using stored functionRef or the current function registry.
 * @returns {{ dashboard: object, changed: boolean, warnings: string[] }}
 */
export function healDashboardChartFunctionIds(dashboard, functions = []) {
    const warnings = [];
    const { byId, byKey } = buildFunctionLookup(functions);
    const charts = dashboard?.layoutJson?.charts ?? [];
    let changed = false;

    const healedCharts = charts.map(chart => {
        if (!isChartLayoutItem(chart)) return chart;

        const currentId = chart.functionId ?? null;
        const ref = chart.functionRef
            ?? lookupDefaultFunctionRef(dashboard?.name, chart)
            ?? null;
        let resolvedId = currentId;

        if (currentId && byId.has(currentId)) {
            const fn = byId.get(currentId);
            const nextRef = functionRefFromRegistered(fn);
            if (nextRef && JSON.stringify(nextRef) !== JSON.stringify(ref)) {
                changed = true;
                return { ...chart, functionRef: nextRef };
            }
            return chart;
        }

        if (ref?.functionName) {
            const fn = byKey.get(functionLookupKey(ref.schema, ref.functionName));
            if (fn) {
                resolvedId = fn.id ?? fn.Id ?? null;
            } else {
                warnings.push(
                    `Chart "${chart.title || chart.id}": function ${ref.schema ?? 'public'}.${ref.functionName} is not registered.`,
                );
            }
        } else if (currentId) {
            warnings.push(
                `Chart "${chart.title || chart.id}": report function ${currentId} is missing — re-add the chart or load defaults.`,
            );
        }

        if (resolvedId && (resolvedId !== currentId || (ref && !chart.functionRef))) {
            changed = true;
            return { ...chart, functionId: resolvedId, functionRef: ref };
        }

        return chart;
    });

    if (!changed) {
        return { dashboard, changed: false, warnings };
    }

    return {
        dashboard: {
            ...dashboard,
            layoutJson: {
                ...(dashboard.layoutJson ?? {}),
                charts: healedCharts,
            },
        },
        changed: true,
        warnings,
    };
}

/** Resolve a chart's functionId against the current tenant function registry. */
export function resolveChartFunctionId(chart, functions = [], dashboardName = null) {
    if (!chart) return null;
    const { dashboard } = healDashboardChartFunctionIds(
        { name: dashboardName, layoutJson: { charts: [chart] } },
        functions,
    );
    return dashboard.layoutJson.charts[0]?.functionId ?? chart.functionId ?? null;
}

/**
 * Build a portable export bundle (no tenant-specific GUIDs).
 * @param {object[]} dashboards
 * @param {object[]} functions - report function list from Redux
 */
export function buildDashboardExportBundle(dashboards, functions = []) {
    const fnById = new Map(
        functions.map(f => [(f.id ?? f.Id), f]),
    );

    const resolveRef = (functionId) => {
        const fn = fnById.get(functionId);
        if (!fn) return null;
        return {
            schema: fn.schema ?? fn.Schema ?? 'public',
            functionName: fn.functionName ?? fn.FunctionName,
            displayName: fn.displayName ?? fn.DisplayName,
        };
    };

    const exportedDashboards = (dashboards ?? []).map(db => {
        const charts = (db.layoutJson?.charts ?? []).map(chart => {
            if (!isChartLayoutItem(chart)) {
                return { ...chart, id: chart.id };
            }
            const { functionId, ...rest } = chart;
            const functionRef = resolveRef(functionId) ?? chart.functionRef ?? null;
            return {
                ...rest,
                id: chart.id,
                functionRef,
            };
        });
        return {
            name: db.name,
            description: db.description ?? null,
            displayOrder: db.displayOrder ?? 0,
            layoutJson: { charts },
        };
    });

    const requiredFunctions = [];
    const seen = new Set();
    for (const db of exportedDashboards) {
        for (const ch of db.layoutJson.charts) {
            if (!isChartLayoutItem(ch)) continue;
            const ref = ch.functionRef;
            if (!ref) continue;
            const key = `${ref.schema}.${ref.functionName}`;
            if (!seen.has(key)) {
                seen.add(key);
                requiredFunctions.push(ref);
            }
        }
    }

    return {
        version: DASHBOARD_EXPORT_VERSION,
        exportedAt: new Date().toISOString(),
        dashboards: exportedDashboards,
        requiredFunctions,
    };
}

/**
 * @param {object} bundle
 * @param {object[]} functions
 * @returns {{ dashboards: object[], warnings: string[] }}
 */
export function parseDashboardImportBundle(bundle, functions = []) {
    const warnings = [];
    if (!bundle || bundle.version !== DASHBOARD_EXPORT_VERSION) {
        throw new Error('Unsupported or invalid dashboard export file (expected version 1).');
    }

    const fnByKey = new Map(
        functions.map(f => {
            const schema = f.schema ?? f.Schema ?? 'public';
            const name = f.functionName ?? f.FunctionName;
            return [`${schema}.${name}`.toLowerCase(), f];
        }),
    );

    const dashboards = (bundle.dashboards ?? []).map(db => {
        const charts = (db.layoutJson?.charts ?? []).map(chart => {
            if (!isChartLayoutItem(chart)) {
                return { ...chart, id: chart.id ?? crypto.randomUUID() };
            }
            const ref = chart.functionRef;
            let functionId = chart.functionId ?? null;
            if (ref) {
                const key = `${ref.schema}.${ref.functionName}`.toLowerCase();
                const fn = fnByKey.get(key);
                if (fn) {
                    functionId = fn.id ?? fn.Id;
                } else {
                    warnings.push(
                        `Chart "${chart.title || chart.id}": function ${ref.schema}.${ref.functionName} not found in this tenant — deploy it in SQL Lab first.`,
                    );
                }
            } else if (!functionId) {
                warnings.push(`Chart "${chart.title || chart.id}": missing function reference.`);
            }

            const { functionRef, ...rest } = chart;
            return {
                ...rest,
                id: chart.id ?? crypto.randomUUID(),
                functionId,
                functionRef: ref ?? chart.functionRef ?? null,
            };
        });

        return {
            name: db.name || 'Imported dashboard',
            description: db.description,
            displayOrder: db.displayOrder ?? 0,
            layoutJson: {
                ...(db.layoutJson ?? {}),
                charts,
            },
        };
    });

    return { dashboards, warnings };
}

function normalizeDashboardName(name) {
    return (name ?? '').trim().toLowerCase();
}

/**
 * Split imported dashboards into new vs name collisions with existing tenant dashboards.
 * @returns {{ toCreate: object[], duplicates: Array<{ imported: object, existing: object }> }}
 */
export function classifyDashboardImportDuplicates(importedDashboards = [], existingDashboards = []) {
    const byName = new Map();
    for (const db of existingDashboards) {
        const key = normalizeDashboardName(db.name);
        if (key) byName.set(key, db);
    }

    const toCreate = [];
    const duplicates = [];

    for (const imp of importedDashboards) {
        const key = normalizeDashboardName(imp.name);
        const existing = key ? byName.get(key) : null;
        if (existing) {
            duplicates.push({ imported: imp, existing });
        } else {
            toCreate.push(imp);
            if (key) byName.set(key, imp);
        }
    }

    return { toCreate, duplicates };
}

/** @typedef {'skip' | 'override'} ImportDuplicatePolicy */

/**
 * @param {object[]} importedDashboards
 * @param {object[]} existingDashboards
 * @param {ImportDuplicatePolicy} duplicatePolicy
 */
export function planDashboardImport(importedDashboards, existingDashboards, duplicatePolicy = 'skip') {
    const { toCreate, duplicates } = classifyDashboardImportDuplicates(importedDashboards, existingDashboards);
    const toOverride = duplicatePolicy === 'override' ? duplicates : [];
    const skipped = duplicatePolicy === 'skip' ? duplicates.length : 0;
    return { toCreate, toOverride, skipped, duplicates };
}

export function downloadJsonFile(filename, data) {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}
